import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getHtmlPage } from "../utils/apiClient";
import AppConstants from "../utils/appConstants";
import "./css/AgreementDetailsModal.css";

export default function AgreementDetailsModal({
  isOpen,
  onClose,
  agreementKey,
  agreementName,
  userId,
  closeLabel,
}) {
  const { i18n, t } = useTranslation();
  const { t: tMobile } = useTranslation("mobile");
  const [htmlContent, setHtmlContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !agreementKey) {
      setHtmlContent("");
      setError(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    const fetchAgreement = async () => {
      setLoading(true);
      setError(null);
      setHtmlContent("");

      try {
        const languageCode = (i18n.language || "en").split("-")[0].toLowerCase();
        const email =
          localStorage.getItem("userEmail") ||
          localStorage.getItem("registrationEmail") ||
          null;

        const response = await getHtmlPage(languageCode, agreementKey, {
          userId,
          email,
        });

        if (cancelled) return;

        const result = response?.data || response?.decryptedData || {};
        const responseCode = result?.CODE;

        if (Number(responseCode) === AppConstants.responseOk) {
          const content =
            response?.htmlContent ||
            result?.DATA ||
            result?.HTML ||
            "";
          setHtmlContent(content);
          if (!content) {
            setError(t("agreementPage.noContent") || "No content available for this agreement.");
          }
        } else {
          setError(
            t("agreementPage.errors.fetchFailed") ||
              "Failed to load agreement content. Please try again."
          );
        }
      } catch {
        if (!cancelled) {
          setError(
            t("agreementPage.errors.fetchFailed") ||
              "Failed to load agreement content. Please try again."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAgreement();

    return () => {
      cancelled = true;
    };
  }, [isOpen, agreementKey, userId, i18n.language, t]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="agreement-details-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="agreement-details-title"
      onClick={onClose}
    >
      <div className="agreement-details-modal" onClick={(e) => e.stopPropagation()}>
        <div className="agreement-details-modal__header">
          <h3 id="agreement-details-title">{agreementName || "Agreement"}</h3>
        </div>

        <div className="agreement-details-modal__body">
          {loading ? (
            <div className="agreement-details-modal__loading">
              {tMobile("loading") || t("common.loading") || "Loading..."}
            </div>
          ) : error ? (
            <div className="agreement-details-modal__error">{error}</div>
          ) : (
            <div
              className="agreement-details-modal__content"
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
          )}
        </div>

        <div className="agreement-details-modal__footer">
          <button
            type="button"
            className="agreement-details-modal__cancel"
            onClick={onClose}
          >
            {closeLabel || tMobile("cancel") || t("common.cancel") || "Close"}
          </button>
        </div>
      </div>
    </div>
  );
}
